// A chat message's `attachment.link` (link kinds step 2): cleaned to stc-chat's limits before it is sent (stc-chat
// silently drops a link that breaks one), read defensively when it arrives, and asked of the player registry — a
// card shows only for an http(s) url of a kind the registry knows (Android `toMesLink` / `ChatLinkRow`).
import type { LinkPreview } from "../api/content";
import { KINDS, knownKindOf, type PlayableItem, type PlayerKind } from "../player";
import type { ChatLink, ChatPreview } from "../types/chat";

/** stc-chat's `attachment.link` limits. */
export const CHAT_LINK_LIMITS = { url: 2048, title: 300, provider: 32, codec: 64, externalId: 128 } as const;
const TYPE_RE = /^[A-Z_]{1,32}$/;

export function isHttpUrl(v: unknown, max: number = CHAT_LINK_LIMITS.url): v is string {
  if (typeof v !== "string" || v.length === 0 || v.length > max) return false;
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() !== "" ? v : undefined);
const within = (v: unknown, max: number): string | undefined => {
  const s = str(v);
  return s != null && s.length <= max ? s : undefined;
};

/**
 * The link as stc-chat stores it, or null when it cannot be one (no http(s) url ≤ 2048, no type or provider):
 * title cut to 300, a thumbnail / codec / external id that breaks a limit left out.
 */
export function toChatLink(p: Partial<Record<keyof LinkPreview, unknown>> | null | undefined): ChatLink | null {
  if (!p || typeof p !== "object") return null;
  const type = str(p.type);
  const provider = within(p.provider, CHAT_LINK_LIMITS.provider);
  if (!type || !TYPE_RE.test(type) || !provider || !isHttpUrl(p.url)) return null;
  const link: ChatLink = { type, url: p.url, provider, live: p.live === true };
  const title = str(p.title);
  if (title) link.title = title.slice(0, CHAT_LINK_LIMITS.title);
  if (isHttpUrl(p.thumbnailUrl)) link.thumbnailUrl = p.thumbnailUrl;
  const codec = within(p.codec, CHAT_LINK_LIMITS.codec);
  if (codec) link.codec = codec;
  const externalId = within(p.externalId, CHAT_LINK_LIMITS.externalId);
  if (externalId) link.externalId = externalId;
  return link;
}

/** A received `attachment.link` (PubNub or history), any shape: the same cleaning, null when it is not a link. */
export const readChatLink = (raw: unknown): ChatLink | null =>
  raw && typeof raw === "object" ? toChatLink(raw as Partial<Record<keyof LinkPreview, unknown>>) : null;

/** The link as the player module's item. */
export const chatLinkItem = (link: ChatLink): PlayableItem => ({
  type: link.type,
  provider: link.provider ?? null,
  codec: link.codec ?? null,
  live: link.live,
  url: link.url,
  externalId: link.externalId ?? null,
  title: link.title ?? null,
});

/**
 * The kind that plays this link in the dock; null (plain text) for a non-http(s) url or a `type` the registry lacks.
 * kindOf goes by provider first, so the type is checked too: `{type: "HOLOGRAM", provider: "youtube"}` is no card.
 * A type of the kind's provider counts (a live `stream` is a radio whatever its stream type).
 */
export function chatLinkKind(link: ChatLink | null | undefined): PlayerKind | null {
  if (!link || !isHttpUrl(link.url)) return null;
  const kind = knownKindOf(chatLinkItem(link));
  if (!kind || !kind.caps.dockable) return null;
  const typeKnown =
    kind.types.includes(link.type) ||
    (kind.provider != null && KINDS.some((k) => k.provider === kind.provider && k.types.includes(link.type)));
  return typeKnown ? kind : null;
}

const optStr = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const optDim = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null);
const httpsUrl = (v: unknown): string | null => (typeof v === "string" && v.length <= CHAT_LINK_LIMITS.url && /^https:\/\//i.test(v) && isHttpUrl(v) ? v : null);

/**
 * A preview card's url: http(s), no userinfo (`user@` / `user:pass@`) and no backslash, so the host the card shows is
 * the host a tap opens on every client (the same rule as file-stc, stc-chat and Android `MesPreview.sanitized`).
 */
export function isPreviewUrl(v: unknown): v is string {
  if (!isHttpUrl(v) || v.includes("\\")) return false;
  const u = new URL(v);
  return u.username === "" && u.password === "";
}

/** A website preview (file-stc's answer or a received `attachment.preview`), any shape: null without a preview url (see `isPreviewUrl`) and a title; an image that is not https is dropped. */
export function toChatPreview(raw: unknown): ChatPreview | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Record<string, unknown>;
  const title = optStr(p.title);
  if (!title || !isPreviewUrl(p.url)) return null;
  const imageUrl = httpsUrl(p.imageUrl);
  return {
    url: p.url,
    title,
    description: optStr(p.description),
    siteName: optStr(p.siteName),
    imageUrl,
    imageWidth: imageUrl ? optDim(p.imageWidth) : null,
    imageHeight: imageUrl ? optDim(p.imageHeight) : null,
  };
}

/**
 * A received message's preview card (PubNub or history), read from its whole `attachment`: a link wins and media never
 * has a preview, so either one means no card (as Android `MessageToRowTool.previewOf`).
 */
export function readChatPreview(attachment: { link?: unknown; media?: unknown; preview?: unknown } | null | undefined): ChatPreview | null {
  if (!attachment || readChatLink(attachment.link) || (Array.isArray(attachment.media) && attachment.media.length > 0)) return null;
  return toChatPreview(attachment.preview);
}

/** The host a preview card shows: lower case, without a leading `www.`; the url itself when it does not parse. */
export function previewHost(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return url;
  }
}
